import { css } from "@emotion/css"
import { useParams } from "react-router"
import { useI18n } from "providers/I18n"
import { useSendFeedBack } from "components/FeedbackSnackbar"
import { useConfirmation } from "components/Confirmation"

import { useMutation, queryCache } from "react-query"
import { Supplier, SupplierStatus } from "models/supplier"

import {
  activateSupplier,
  deactivateSupplier,
  dissocMediaOwnerSupplier,
  dissocSupplierMediaOwener,
} from "api/suppliers"

import { CellProps, Column } from "react-table"
import { Link } from "components/Link"
import { Status } from "components/Status"
import { Truncated } from "components/Truncated"
import { ColorMap } from "utils/color"

import MoreActions from "components/MoreActions"
import { ReactComponent as Deactivate } from "assets/icon-deactivate.svg"
import { ReactComponent as CheckIcon } from "assets/check.svg"
import { useAuthenticated } from "providers/A12n"

export const columns: Array<Column<Supplier>> = [
  {
    fixed: true,
    default: true,
    sortable: true,
    header: "vendors.columns.name",
    id: "name",
    Cell: function (props: CellProps<Supplier>) {
      const { id, name, localName } = props.row.original
      const { hasAuthority } = useAuthenticated()
      return (
        <div>
          {hasAuthority([
            "AUTH_ACCESS_AGENCY_ADMIN",
            "AUTH_ACCESS_AGENCY_FINANCE",
          ]) ? (
            <Link color="primary" to={`/agency-vendors/${id}/profile`}>
              <Truncated maxWidth={250}>{name}</Truncated>
            </Link>
          ) : (
            <Link color="primary" to={`/market-vendors/${id}/profile`}>
              <Truncated maxWidth={250}>{name}</Truncated>
            </Link>
          )}
          <div>
            <Truncated
              maxWidth={250}
              className={css`
                font-size: 11px;
                color: var(--dark-grey);
              `}
            >
              {localName ?? ""}
            </Truncated>
          </div>
        </div>
      )
    },
  },
  {
    default: true,
    sortable: false,
    header: "vendors.fields.code",
    accessor: "code",
  },
  {
    default: true,
    sortable: false,
    header: "vendors.columns.status",
    accessor: "status",
    Cell: function (props: CellProps<Supplier>) {
      const { value: status } = props
      const { t } = useI18n()
      return status === null ? null : (
        <Status color={ColorMap[status]}>{t(`status.${status ?? ""}`)}</Status>
      )
    },
  },
  {
    default: true,
    sortable: false,
    header: "vendors.columns.vendorgroup",
    id: "groupName",
    accessor: (x) => x.groupName ?? "-",
  },
]

function ActivateDeactivateActions(props: {
  id: string
  status: SupplierStatus
  queryKey: string
}) {
  const { id, status, queryKey } = props
  const { t } = useI18n()
  const feedback = useSendFeedBack()
  const confirm = useConfirmation()
  const { hasAuthority } = useAuthenticated()

  const [activate] = useMutation(activateSupplier, {
    onSuccess: () => {
      feedback(t("vendors.activate.success"))
      queryCache.invalidateQueries(queryKey)
    },
  })

  const deactivate = () => {
    confirm(
      {
        action: () => deactivateSupplier(id),
        onError: (e) => {
          feedback(t(e.message) || t("vendors.deactivate.error"), "error")
          queryCache.invalidateQueries(queryKey)
        },
        onSuccess: () => {
          feedback(t("vendors.deactivate.success"))
          queryCache.invalidateQueries(queryKey)
        },
      },
      {
        type: "primary_destructive",
        submitLabel: t("actions.deactivate"),
        cancelLabel: t("actions.cancel"),

        title: t("vendors.deactivate.title"),
        description: t("vendors.deactivate.description"),
      },
    )
  }

  return (
    <MoreActions
      options={[
        // ACTIVATE
        {
          label: t("actions.activate"),
          icon: <CheckIcon />,
          onClick: () => {
            //
            activate(id)
          },
          hidden: status === "ACTIVE" || !hasAuthority(["AUTH_EDIT_SUPPLIER"]),
        },
        // DEACTIVATE
        {
          label: t("actions.deactivate"),
          icon: <Deactivate />,
          onClick: deactivate,
          hidden: status !== "ACTIVE" || !hasAuthority(["AUTH_EDIT_SUPPLIER"]),
        },
      ]}
    />
  )
}

export function getActivateDeactivateActionsColumn(opts: { queryKey: string }) {
  return {
    default: true,
    sortable: false,
    fixed: true,
    header: "columns.actions",
    id: "actions",
    Cell: function (props: CellProps<Supplier>) {
      const { id, status } = props.row.original
      return (
        <ActivateDeactivateActions
          id={id}
          status={status}
          queryKey={opts.queryKey}
        />
      )
    },
  }
}

/*

*/

function DissociateActions(props: {
  id: string
  queryKey: string
  assocQueryKey: string
  status?: SupplierStatus
}) {
  const { id: supplierIdTo, queryKey, assocQueryKey, status } = props
  const { id: supplierIdFrom } = useParams<{ id: string }>()
  const { t } = useI18n()
  const feedback = useSendFeedBack()
  const confirm = useConfirmation()
  const { hasAuthority } = useAuthenticated()
  const isConnectedVendor = queryKey === "connected-vendors"
  const vendors = isConnectedVendor ? "vendors" : "mediaOwner"

  const [dissociateSupplier] = useMutation(dissocSupplierMediaOwener)
  const [dissociateMediaOwner] = useMutation(dissocMediaOwnerSupplier)

  const action = () => {
    confirm(
      {
        action: isConnectedVendor
          ? () =>
              dissociateSupplier({
                mediaOwnerId: supplierIdFrom,
                supplierId: supplierIdTo,
              })
          : () =>
              dissociateMediaOwner({
                mediaOwnerId: supplierIdTo,
                supplierId: supplierIdFrom,
              }),
        onError: (e) => {
          feedback(t(e.message) || t(`${vendors}.dissociate.error`), "error")
          queryCache.invalidateQueries(queryKey)
          queryCache.invalidateQueries(assocQueryKey)
        },
        onSuccess: () => {
          feedback(t(`${vendors}.dissociate.success`))
          queryCache.invalidateQueries(queryKey)
          queryCache.invalidateQueries(assocQueryKey)
        },
      },
      {
        type: "primary",
        submitLabel: t(`${vendors}.dissociate.submit`),
        cancelLabel: t("actions.cancel"),

        title: t(`${vendors}.dissociate.title`),
        description: t(`${vendors}.dissociate.description`),
      },
    )
  }

  return (
    <MoreActions
      options={[
        {
          label: t(`${vendors}.actions.dissociate`),
          icon: <Deactivate />,
          onClick: action,
          hidden:
            status === "INACTIVE" ||
            !hasAuthority(["AUTH_REMOVE_AGENCY_SUPPLIER"]),
        },
      ]}
    />
  )
}

export function getDissociateActionsColumn(opts: {
  queryKey: string
  assocQueryKey: string
  status?: SupplierStatus
}) {
  return {
    default: true,
    sortable: false,
    fixed: true,
    header: "columns.actions",
    id: "actions",
    Cell: function (props: CellProps<Supplier>) {
      const { id } = props.row.original
      return (
        <DissociateActions
          id={id}
          queryKey={opts.queryKey}
          assocQueryKey={opts.assocQueryKey}
          status={opts?.status}
        />
      )
    },
  }
}
